import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Link,
  Paper,
  useMediaQuery,
} from '@mui/material';
import {
  DoseAlternative,
  DosePhase,
  DrugSubstanceDose,
  formatDose,
  formatDuration,
  formatInterval,
  Source,
} from '../../../types/DrugSubstanceDose.type';

// Sources link component
const SourcesLink: React.FC<{ sources: Source[] }> = ({ sources }) => {
  if (!sources || sources.length === 0) return null;

  const handleClick = () => {
    // Could open a modal or tooltip with source details
    const sourceNames = sources.map((s) => s.name).join(', ');
    alert(`Sources: ${sourceNames}`);
  };

  return (
    <Link
      component="button"
      variant="body2"
      onClick={handleClick}
      sx={{
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': { textDecoration: 'underline' },
        ml: 1,
      }}
    >
      Sources
    </Link>
  );
};

// Single phase row (mobile version)
const PhaseRowMobile: React.FC<{
  phase: DosePhase;
  isLast: boolean;
  onSelectCalculate?: () => void;
  showButton: boolean;
}> = ({ phase, isLast, onSelectCalculate, showButton }) => (
  <Box
    sx={{
      mb: 2,
      pb: isLast ? 0 : 2,
      borderBottom: isLast ? 'none' : 1,
      borderColor: 'divider',
    }}
  >
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}
      >
        TYPE
      </Typography>
      <Typography variant="body2">{phase.dose_type.name}</Typography>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}
      >
        ROUTE
      </Typography>
      <Typography variant="body2">
        {phase.drug_administration_route.name}
      </Typography>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}
      >
        DOSE
      </Typography>
      <Typography variant="body2">{formatDose(phase)}</Typography>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}
      >
        INTERVAL
      </Typography>
      <Typography variant="body2">{formatInterval(phase)}</Typography>
    </Box>
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ fontWeight: 'bold', letterSpacing: 1.5 }}
      >
        DURATION
      </Typography>
      <Typography variant="body2">{formatDuration(phase)}</Typography>
    </Box>
    {isLast && showButton && onSelectCalculate && (
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={onSelectCalculate}
          size="small"
        >
          Select & Calculate
        </Button>
      </Box>
    )}
  </Box>
);

// Single phase row (desktop version)
const PhaseRowDesktop: React.FC<{
  phase: DosePhase;
  isLast: boolean;
  onSelectCalculate?: () => void;
  showButton: boolean;
}> = ({ phase, isLast, onSelectCalculate, showButton }) => (
  <TableRow>
    <TableCell
      sx={{ borderBottom: isLast ? 1 : 'none', borderColor: 'divider', py: 1 }}
    >
      {phase.dose_type.name}
    </TableCell>
    <TableCell
      sx={{ borderBottom: isLast ? 1 : 'none', borderColor: 'divider', py: 1 }}
    >
      {phase.drug_administration_route.name}
    </TableCell>
    <TableCell
      sx={{ borderBottom: isLast ? 1 : 'none', borderColor: 'divider', py: 1 }}
    >
      {formatDose(phase)}
    </TableCell>
    <TableCell
      sx={{ borderBottom: isLast ? 1 : 'none', borderColor: 'divider', py: 1 }}
    >
      {formatInterval(phase)}
    </TableCell>
    <TableCell
      sx={{ borderBottom: isLast ? 1 : 'none', borderColor: 'divider', py: 1 }}
    >
      {formatDuration(phase)}
    </TableCell>
    <TableCell
      sx={{
        borderBottom: isLast ? 1 : 'none',
        borderColor: 'divider',
        py: 1,
        textAlign: 'right',
      }}
    >
      {isLast && showButton && onSelectCalculate && (
        <Button
          variant="outlined"
          color="primary"
          onClick={onSelectCalculate}
          size="small"
        >
          Select & Calculate
        </Button>
      )}
    </TableCell>
  </TableRow>
);

// Alternative section
interface AlternativeRowsProps {
  alternative: DoseAlternative;
  alternativeIndex: number;
  onSelectCalculate?: (alternative: DoseAlternative) => void;
  mobile: boolean;
}

const AlternativeRows: React.FC<AlternativeRowsProps> = ({
  alternative,
  alternativeIndex,
  onSelectCalculate,
  mobile,
}) => {
  const phases = alternative.phases || [];

  if (mobile) {
    return (
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            letterSpacing: 1.5,
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          ALTERNATIVE {alternativeIndex + 1}
          <SourcesLink sources={alternative.sources} />
        </Typography>
        {phases.map((phase, index) => (
          <PhaseRowMobile
            key={phase.id || index}
            phase={phase}
            isLast={index === phases.length - 1}
            onSelectCalculate={() => onSelectCalculate?.(alternative)}
            showButton={!!onSelectCalculate}
          />
        ))}
      </Box>
    );
  }

  return (
    <>
      <TableRow>
        <TableCell
          colSpan={6}
          sx={{
            borderBottom: 'none',
            pt: 2,
            pb: 0,
            fontWeight: 'bold',
            letterSpacing: 1.5,
            color: 'text.secondary',
          }}
        >
          ALTERNATIVE {alternativeIndex + 1}
          <SourcesLink sources={alternative.sources} />
        </TableCell>
      </TableRow>
      {phases.map((phase, index) => (
        <PhaseRowDesktop
          key={phase.id || index}
          phase={phase}
          isLast={index === phases.length - 1}
          onSelectCalculate={() => onSelectCalculate?.(alternative)}
          showButton={!!onSelectCalculate}
        />
      ))}
    </>
  );
};

// Main table component for a drug substance dose
export interface DrugDosageAlternativesTableProps {
  dose: DrugSubstanceDose;
  drugSubstanceName: string;
  onSelectAlternative?: (
    alternative: DoseAlternative,
    dose: DrugSubstanceDose
  ) => void;
}

export const DrugDosageAlternativesTable: React.FC<
  DrugDosageAlternativesTableProps
> = ({ dose, drugSubstanceName, onSelectAlternative }) => {
  const mobile = useMediaQuery('(max-width:600px)');
  const alternatives = dose.alternatives || [];

  return (
    <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {drugSubstanceName}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          {dose.drug_indication.name}
        </Typography>
        <SourcesLink sources={dose.sources} />
      </Box>

      {/* Dosage specification / info */}
      {(dose.dosage_specification || dose.info) && (
        <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
          {dose.dosage_specification && (
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-line', mb: dose.info ? 2 : 0 }}
            >
              {dose.dosage_specification}
            </Typography>
          )}
          {dose.info && (
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {dose.info}
            </Typography>
          )}
        </Box>
      )}

      {/* Alternatives table */}
      {alternatives.length > 0 && (
        <Box sx={{ p: 2 }}>
          {mobile ? (
            <Box>
              {/* Table header for mobile */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 1,
                  mb: 2,
                  pb: 1,
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {['TYPE', 'ROUTE', 'DOSE', 'INTERVAL', 'DURATION'].map(
                  (header) => (
                    <Typography
                      key={header}
                      variant="caption"
                      sx={{ fontWeight: 'bold', letterSpacing: 1 }}
                    >
                      {header}
                    </Typography>
                  )
                )}
              </Box>
              {alternatives.map((alternative, index) => (
                <AlternativeRows
                  key={alternative.id || index}
                  alternative={alternative}
                  alternativeIndex={index}
                  onSelectCalculate={
                    onSelectAlternative
                      ? (alt) => onSelectAlternative(alt, dose)
                      : undefined
                  }
                  mobile={true}
                />
              ))}
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {/* Header row */}
                  <TableRow>
                    {['TYPE', 'ROUTE', 'DOSE', 'INTERVAL', 'DURATION', ''].map(
                      (header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: 'bold',
                            letterSpacing: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            py: 1,
                          }}
                        >
                          {header}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                  {alternatives.map((alternative, index) => (
                    <AlternativeRows
                      key={alternative.id || index}
                      alternative={alternative}
                      alternativeIndex={index}
                      onSelectCalculate={
                        onSelectAlternative
                          ? (alt) => onSelectAlternative(alt, dose)
                          : undefined
                      }
                      mobile={false}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {alternatives.length === 0 && (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No dosage alternatives available.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default DrugDosageAlternativesTable;
